import axios from "axios";
import {
  CustomListResponse,
  TotalMembersResponse,
  DeleteCustomListResponse,
  UpdateCustomListStatusResponse,
} from "src/layouts/Engagements/EngagementInterfaces";
import { getApiBaseUrl } from "src/config/endpoints";
import i18n from "src/i18n/config";
import { normalizeApiLang } from "src/utils/apiLang";
import { getApiKey, getTenantId } from "src/utils/getters";

const API_BASE_URL = getApiBaseUrl();

// ========== Get Total Lists API ==========
export const getCustomLists = async (): Promise<CustomListResponse> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const searchParams = new URLSearchParams({
    tenant_id: tenantId,
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const url = `${API_BASE_URL}/engagements/view-custom-lists?${searchParams.toString()}`;

  const response = await axios.post<CustomListResponse>(
    url,
    {},
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// ========== Get Total Members API ==========

export const getTotalMembers = async (): Promise<TotalMembersResponse> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const searchParams = new URLSearchParams({
    tenant_id: tenantId,
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const response = await axios.post<TotalMembersResponse>(
    `${API_BASE_URL}/engagements/total-members?${searchParams.toString()}`,
    {}, // empty body
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// ========== MODIFY LIST API ==========
export const uploadModifiedCustomList = async (
  customListId: number,
  file: File
): Promise<void> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const formData = new FormData();
  formData.append("file", file);
  formData.append("tenant_id", tenantId);
  formData.append("custom_list_id", customListId.toString());

  const searchParams = new URLSearchParams({
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const response = await fetch(
    `${API_BASE_URL}/engagements/update-custom-list?${searchParams.toString()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error("Failed to save changes");
  }
};

// ========== GET CSV CONTENT FOR  API ==========
export const getCustomListCSV = async (listId: number): Promise<Blob> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const searchParams = new URLSearchParams({
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const response = await fetch(
    `${API_BASE_URL}/engagements/edit-custom-list?${searchParams.toString()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        tenant_id: tenantId,
        custom_list_id: listId,
      }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch CSV for custom list");
  }

  return await response.blob();
};

// ========== DELETE LIST API ==========
export const deleteCustomList = async (
  selectedListId: number
): Promise<DeleteCustomListResponse> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const payload = {
    tenant_id: tenantId,
    custom_list_ids: [selectedListId],
  };

  const searchParams = new URLSearchParams({
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const response = await axios.post<DeleteCustomListResponse>(
    `${API_BASE_URL}/engagements/delete-custom-list?${searchParams.toString()}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const updateCustomListStatus = async (
  selectedListId: number,
  actionType: "archive" | "active"
): Promise<UpdateCustomListStatusResponse> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const payload = {
    tenant_id: tenantId,
    custom_list_ids: [selectedListId],
    status: actionType,
  };

  const searchParams = new URLSearchParams({
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const response = await axios.put<UpdateCustomListStatusResponse>(
    `${API_BASE_URL}/engagements/update-custom-list-status?${searchParams.toString()}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// ========== Bulk update==========
export const bulkDeleteCustomLists = async (
  tenant_id: string,
  custom_list_ids: number[]
) => {
  const searchParams = new URLSearchParams({
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const response = await axios.post(
    `${API_BASE_URL}/engagements/delete-custom-list?${searchParams.toString()}`,
    {
      tenant_id,
      custom_list_ids,
    },
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const bulkUpdateCustomListStatus = async (
  tenant_id: string,
  custom_list_ids: number[],
  status: "archive" | "active"
) => {
  const searchParams = new URLSearchParams({
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  });
  const response = await axios.put(
    `${API_BASE_URL}/engagements/update-custom-list-status?${searchParams.toString()}`,
    {
      tenant_id,
      custom_list_ids,
      status,
    },
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};
